/**
 * use-typewriter — ported VERBATIM from the production play page's typewriter
 * reveal (moonshort-backend `app/(player)/play/[sessionId]/page.tsx`, the
 * `revealCount` effect + `isTyping` + `completeReveal` + `handleTap`'s
 * "complete-on-tap" rule).
 *
 * Reveals `fullText` at ~60 chars/sec using requestAnimationFrame, exposing the
 * visible character count and an `isTyping` flag. A tap while typing should
 * call `complete()` to finish the reveal instead of advancing (the host store
 * wires that into its tap handler).
 *
 * The blinking caret + ready chevron are NOT drawn here — `DialogueBubble`
 * (vendored primitives.tsx) renders them off the `isTyping` flag.
 */
import { useEffect, useRef, useState } from "react";
// Typewriter speed — characters per second. Verbatim TYPEWRITER_CPS = 60.
const TYPEWRITER_CPS = 60;
/**
 * @param fullText The line currently being revealed. Pass "" when there is no
 *   active dialogue/narrator/you line (phone lines and decisions have no
 *   typewriter). The reveal restarts whenever this value changes — mirroring
 *   the server effect keyed on `lineText`.
 */
export function useTypewriter(fullText) {
    const [revealCount, setRevealCount] = useState(0);
    const revealAccumRef = useRef(0);
    const revealTargetRef = useRef(0);
    const revealRafRef = useRef(null);
    useEffect(() => {
        const full = fullText;
        if (revealRafRef.current !== null) {
            cancelAnimationFrame(revealRafRef.current);
            revealRafRef.current = null;
        }
        if (!full) {
            revealTargetRef.current = 0;
            revealAccumRef.current = 0;
            setRevealCount(0);
            return;
        }
        revealTargetRef.current = full.length;
        revealAccumRef.current = 0;
        setRevealCount(0);
        let last = null;
        const charsPerMs = TYPEWRITER_CPS / 1000;
        const tick = (now) => {
            if (last === null)
                last = now;
            const dt = now - last;
            last = now;
            revealAccumRef.current = Math.min(revealTargetRef.current, revealAccumRef.current + dt * charsPerMs);
            setRevealCount(Math.floor(revealAccumRef.current));
            if (revealAccumRef.current < revealTargetRef.current) {
                revealRafRef.current = requestAnimationFrame(tick);
            }
            else {
                revealRafRef.current = null;
            }
        };
        revealRafRef.current = requestAnimationFrame(tick);
        return () => {
            if (revealRafRef.current !== null) {
                cancelAnimationFrame(revealRafRef.current);
                revealRafRef.current = null;
            }
        };
    }, [fullText]);
    const isTyping = fullText.length > 0 && revealCount < revealTargetRef.current;
    function complete() {
        if (revealRafRef.current !== null) {
            cancelAnimationFrame(revealRafRef.current);
            revealRafRef.current = null;
        }
        revealAccumRef.current = revealTargetRef.current;
        setRevealCount(revealTargetRef.current);
    }
    return { revealCount, isTyping, complete };
}
